import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { PartsStyleInterpolation } from "@chakra-ui/styled-system";
import { merge } from "lodash-es";

const { defineMultiStyleConfig } = createMultiStyleConfigHelpers([
  "container",
  "control",
  "group",
  "groupContainer",
]);

const defaultBaseTheme: PartsStyleInterpolation = {
  container: {
    bg: "white",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderColor: "gray.300",
    borderWidth: "1px",
    transitionProperty: "common",
    transitionDuration: "normal",
    _notFirst: {
      marginLeft: "-1px",
    },
    _first: {
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
    _last: {
      borderTopRightRadius: "4px",
      borderBottomRightRadius: "4px",
    },
    _notLast: {
      borderRightWidth: "1px",
    },
    _hover: {
      backgroundColor: "gray.50",
      // Hack to draw this button on top
      transform: "scale(1)",
      zIndex: 10,
    },
    "&:has(:focus-visible)": {
      shadow: "xsFocusedPrimary100",
    },
    _checked: {
      borderColor: "primary.400",
      backgroundColor: "primary.50",
      // Hack to draw this button on top
      transform: "scale(1)",
      zIndex: 20,
      _active: {
        backgroundColor: "primary.200",
      },
    },
    _invalid: {
      borderColor: "red.400",
    },
    _disabled: {
      pointerEvents: "none",
    },
  },
  control: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    textAlign: "center",
    lineHeight: "20px",
    color: "gray.400",
    _checked: {
      color: "primary.700",
    },
    _indeterminate: {
      color: "gray.500",
    },
  },
  groupContainer: {
    _disabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
};

const ToggleButtonTheme = defineMultiStyleConfig({
  baseStyle: {},
  variants: {
    default: defaultBaseTheme,
    rounded: merge(
      {
        container: {
          _first: {
            borderTopLeftRadius: "16px",
            borderBottomLeftRadius: "16px",
          },
          _last: {
            borderTopRightRadius: "16px",
            borderBottomRightRadius: "16px",
          },
        },
      },
      defaultBaseTheme,
    ),
    timeframe: {
      groupContainer: {
        bg: "gray.100",
        borderRadius: "6px",
      },
      group: {
        mb: 0,
      },
      container: {
        padding: "4px",
      },
      control: {
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        px: "14px",
        color: "gray.600",
        borderRadius: "6px",
        fontSize: "10px",
        lineHeight: "normal",
        whiteSpace: "nowrap",
        ".chakra-icon": {
          display: "none",
        },
        _checked: {
          ".chakra-icon": {
            display: "inline-flex",
          },
          color: "gray.900",
          bg: "white",
          boxShadow:
            "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)",
        },
      },
    },
  },
  sizes: {
    sm: ({ variant }) => {
      if (variant === "timeframe") {
        return {
          control: {
            py: "6px",
          },
        };
      }
      return {
        group: {
          mb: 0,
        },
        container: {
          px: "12px",
          py: "5px",
        },
        control: {
          minWidth: "10px",
        },
      };
    },
    md: {
      container: {
        px: "8px",
        py: "13px",
      },
    },
    lg: ({ variant }) => {
      if (variant === "timeframe") {
        return {
          control: {
            py: "8px",
          },
        };
      }
      return {};
    },
  },
  defaultProps: {
    size: "md",
    variant: "default",
  },
});

export default ToggleButtonTheme;
